import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../../../components/common/header";
import BottomSheet from "../../../../components/common/bottom-sheet";
import { useMutation, useQuery } from "@apollo/client";
import clsx from "clsx";
import { GET_SELLER_STORE } from "../../api/seller-store";
import { UPDATE_RESERVE_TIME } from "../../api/reserve-queries";
import { GET_PACK_UNPROCESSED_ORDER_COUNT } from "../../page";
import { notify } from "../../../../utils/toast";
import ReasonModal from "./components/reason-bottom-sheet";

// 내부적으로 요일은 0 ~ 6으로 관리 (0: 월요일, 1: 화요일, …, 6: 일요일)
const daysOfWeek: number[] = [0, 1, 2, 3, 4, 5, 6];
// UI 표기를 위한 한글 요일 배열
const dayLabels: string[] = ["월", "화", "수", "목", "금", "토", "일"];

interface TimeRange {
  start: string;
  end: string;
  enabled: boolean;
}

type TimeData = Record<number, TimeRange>;

const defaultTimeRange: TimeRange = {
  start: "",
  end: "",
  enabled: false,
};

const isValidTimeRange = (start: string, end: string): boolean => {
  if (!start || !end) return false; // 빈 값 체크 추가

  // 23:30은 예외적으로 허용
  if (start === "23:30" && end === "23:55") return true;

  const [startHour, startMinute] = start.split(":").map(Number);
  const [endHour, endMinute] = end.split(":").map(Number);

  // 유효하지 않은 시간 형식 체크
  if (
    isNaN(startHour) ||
    isNaN(startMinute) ||
    isNaN(endHour) ||
    isNaN(endMinute)
  ) {
    return false;
  }

  const startTotalMinutes = startHour * 60 + startMinute;
  const endTotalMinutes = endHour * 60 + endMinute;

  return endTotalMinutes - startTotalMinutes >= 30;
};

const PickupTimeUpdate: React.FC = () => {
  const navigate = useNavigate();
  const [timeData, setTimeData] = useState<TimeData>(
    daysOfWeek.reduce((acc: TimeData, day: number) => {
      acc[day] = { ...defaultTimeRange };
      return acc;
    }, {} as TimeData),
  );
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedType, setSelectedType] = useState<"start" | "end" | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState<string>("00");
  const [selectedMinute, setSelectedMinute] = useState<string>("00");
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const { data, loading, error } = useQuery(GET_SELLER_STORE, {
    fetchPolicy: "network-only",
  });
  const [updateReserveTime] = useMutation(UPDATE_RESERVE_TIME);
  const { data: packOrderCountData } = useQuery(
    GET_PACK_UNPROCESSED_ORDER_COUNT,
    {
      fetchPolicy: "network-only",
    },
  );
  const hourScrollRef = useRef<HTMLDivElement>(null);
  const minuteScrollRef = useRef<HTMLDivElement>(null);

  // 서버에서 받은 reserveTime 데이터를 state에 초기화
  useEffect(() => {
    if (data && data.getSellerStore && data.getSellerStore.reserveTime) {
      const reserveTimeFromServer = data.getSellerStore.reserveTime;
      const newTimeData: TimeData = {} as TimeData;
      daysOfWeek.forEach((day) => {
        newTimeData[day] = reserveTimeFromServer[day] || {
          ...defaultTimeRange,
        };
      });
      setTimeData(newTimeData);
    }
  }, [data]);

  // 바텀시트가 열릴 때 현재 선택된 시간으로 스크롤 위치 조정
  useEffect(() => {
    if (isModalOpen) {
      // DOM이 완전히 렌더링된 후 스크롤 위치 조정
      setTimeout(() => {
        if (hourScrollRef.current) {
          const hourElement = document.getElementById(
            `hour-item-${selectedHour}`,
          );
          if (hourElement) {
            hourScrollRef.current.scrollTop = hourElement.offsetTop;
          }
        }
        if (minuteScrollRef.current) {
          const minuteElement = document.getElementById(
            `minute-item-${selectedMinute}`,
          );
          if (minuteElement) {
            minuteScrollRef.current.scrollTop = minuteElement.offsetTop;
          }
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  // 매장이 없을 경우 처리
  if (loading) return <p>로딩 중...</p>;
  if (error) return <p>오류: {error.message}</p>;

  if (!data?.getSellerStore) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <p className="text-lg mb-6 text-gray-700">
          매장이 아직 등록되지 않았습니다.
        </p>
        <button
          onClick={() => navigate("/boss/my/store-management")}
          className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition duration-300"
        >
          매장 추가하러 가기
        </button>
      </div>
    );
  }
  const handleToggleEnabled = (day: number) => {
    const today = new Date();
    if (day === today.getDay() - 1 || day === today.getDay()) {
      if (packOrderCountData?.getUnProcessedPackOrderCount > 0) {
        alert(
          "주문 처리가 안 된 예약이 있습니다. 주문처리를 완료하고 시간을 설정해주세요.",
        );
        return;
      }
    }
    setTimeData((prev) => {
      const newData = { ...prev };
      if (!prev[day].enabled) {
        // 활성화할 때 이전 요일의 시간 가져오기
        let prevTime = { start: "", end: "" };
        for (let i = day - 1; i >= 0; i--) {
          if (newData[i].enabled && newData[i].start && newData[i].end) {
            prevTime = {
              start: newData[i].start,
              end: newData[i].end,
            };
            break;
          }
        }
        newData[day] = {
          start: prevTime.start,
          end: prevTime.end,
          enabled: true,
        };
      } else {
        // 비활성화할 때는 시간 데이터 유지
        newData[day] = {
          ...prev[day],
          enabled: false,
        };
      }
      return newData;
    });
  };

  const handleOpenModal = (day: number, type: "start" | "end") => {
    const today = new Date();
    if (day === today.getDay() - 1 || day === today.getDay()) {
      if (packOrderCountData?.getUnProcessedPackOrderCount > 0) {
        alert(
          "주문 처리가 안 된 예약이 있습니다. 주문처리를 완료하고 시간을 설정해주세요.",
        );
        return;
      }
    }

    setSelectedDay(day);
    setSelectedType(type);
    const currentTime = timeData[day]?.[type] || "18:00";
    const [hour, minute] = currentTime.split(":");
    setSelectedHour(hour);
    setSelectedMinute(minute);
    setIsModalOpen(true);
  };

  // 시작 시간을 설정하면 종료 시간을 자동으로 30분 뒤로 계산 (최대 23:55까지)
  const getNextTime = (hour: string, minute: string) => {
    let nextMinute = parseInt(minute) + 60;
    let nextHour = parseInt(hour);
    if (nextMinute >= 60) {
      nextHour += 1;
      nextMinute %= 60;
    }
    if (nextHour >= 24) {
      nextHour = 23;
      nextMinute = 55;
    }
    return `${String(nextHour).padStart(2, "0")}:${String(nextMinute).padStart(
      2,
      "0",
    )}`;
  };

  const handleSaveTime = () => {
    if (selectedDay === null || !selectedType) return;

    const newTime = `${selectedHour}:${selectedMinute}`;

    setTimeData((prev) => {
      const updatedData = { ...prev };
      const currentDayData = { ...updatedData[selectedDay] };

      if (selectedType === "start") {
        currentDayData.start = newTime;

        // 종료 시간이 없거나 유효하지 않은 경우 자동 설정
        if (
          !currentDayData.end ||
          !isValidTimeRange(newTime, currentDayData.end)
        ) {
          const nextTime = getNextTime(selectedHour, selectedMinute);
          currentDayData.end = nextTime;
        }
      } else {
        // 종료 시간 설정 시 유효성 검사
        if (!isValidTimeRange(currentDayData.start, newTime)) {
          // alert를 여기서 제거하고 return으로만 처리
          return prev;
        }
        currentDayData.end = newTime;
      }

      updatedData[selectedDay] = currentDayData;
      return updatedData;
    });

    // 종료 시간 설정 시 유효성 검사를 여기서 한 번만 수행
    if (selectedType === "end") {
      const currentDayData = timeData[selectedDay];
      if (!isValidTimeRange(currentDayData.start, newTime)) {
        alert("종료 시간은 시작 시간보다 최소 30분 늦어야 합니다!");
        return;
      }
    }

    setIsModalOpen(false);
  };

  // 모든 요일이 비활성화되었을 때 이유를 선택하는 모달에서 이유를 제출하면 실행
  const handleReasonSubmit = (reason: string) => {
    // 이유와 함께 서버에 전달
    if (window.confirm("모든 요일에 예약 주문을 받지 않도록 설정할까요?")) {
      updateReserveTime({
        variables: {
          id: data?.getSellerStore.id,
          reserveTime: timeData,
          reason: reason, // 이유 전달
        },
        update: (cache, { data: { updateReserveTime } }) => {
          cache.modify({
            fields: {
              getSellerStore: (existingStore = {}) => ({
                ...existingStore,
                reserveTime: updateReserveTime.reserveTime,
              }),
            },
          });
        },
        onCompleted: () => {
          navigate("/boss/pre-order/home");
          notify("픽업시간이 저장되었습니다");
          setIsReasonModalOpen(false);
        },
      });
    }
  };

  const handleSubmit = () => {
    // 활성화된 요일의 시간 설정 검증
    const invalidDays = Object.entries(timeData)
      .filter(([_, range]) => {
        return (
          range.enabled &&
          (!range.start ||
            !range.end ||
            !isValidTimeRange(range.start, range.end))
        );
      })
      .map(([day]) => dayLabels[Number(day)]);

    if (invalidDays.length > 0) {
      alert(
        `${invalidDays.join(
          ", ",
        )}요일의 시간이 올바르지 않습니다. 다시 설정해주세요.`,
      );
      return;
    }

    // 모든 요일이 비활성화되었는지 확인
    const allDaysDisabled = Object.values(timeData).every(
      (range) => !range.enabled,
    );
    if (allDaysDisabled) {
      setIsReasonModalOpen(true);
      return;
    }

    if (window.confirm("픽업시간을 저장할까요?")) {
      updateReserveTime({
        variables: { id: data?.getSellerStore.id, reserveTime: timeData },
        update: (cache, { data: { updateReserveTime } }) => {
          // Update the cache for getSellerStore query
          // preorder 헤더에 반영
          cache.modify({
            fields: {
              getSellerStore: (existingStore = {}) => ({
                ...existingStore,
                reserveTime: updateReserveTime.reserveTime,
              }),
            },
          });
        },
        onCompleted: () => {
          navigate("/boss/pre-order/home");
          notify("픽업시간이 저장되었습니다");
        },
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header
        title="예약 픽업시간 설정"
        showBackButton
        onBackClick={() => navigate("/boss/pre-order/home", { replace: true })}
      />

      <div className="mt-6 mx-4">
        <div className="text-center text-lg font-medium mb-4 break-keep">
          <div>상품 픽업을 언제하면 좋을까요?</div>
          <div className="mt-1">픽업시간을 설정해주세요</div>

          <div className="mt-1">
            체크를 해지하시면 그 요일에는 오픈하지 않아요!
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md">
          {daysOfWeek.map((day) => {
            const dayData = timeData[day] || { ...defaultTimeRange };
            return (
              <div
                key={day}
                className="flex items-center justify-between py-2 border-b last:border-none"
              >
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={dayData.enabled}
                    onChange={() => handleToggleEnabled(day)}
                    className="w-5 h-5 accent-blue-500"
                  />
                  <span className="text-lg font-medium">{dayLabels[day]}</span>
                </div>
                {dayData.enabled ? (
                  <div className="flex items-center space-x-2">
                    <button
                      className="border rounded p-2 w-24 bg-green-200 text-center"
                      onClick={() => handleOpenModal(day, "start")}
                    >
                      {dayData.start || "시작"}
                    </button>
                    <span>~</span>
                    <button
                      className={clsx(
                        "border rounded p-2 w-24 bg-green-200 text-center",
                        { "opacity-30": !dayData.start },
                      )}
                      onClick={() =>
                        dayData.start && handleOpenModal(day, "end")
                      }
                    >
                      {dayData.end || "종료"}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2 opacity-50">
                    <div className="border rounded p-2 w-24 bg-gray-200 text-center">
                      {dayData.start || "시작"}
                    </div>
                    <span>~</span>
                    <div className="border rounded p-2 w-24 bg-gray-200 text-center">
                      {dayData.end || "종료"}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex justify-center mt-4 gap-2">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-gray-200"></div>
              <span className="text-gray-600">비활성화 시간</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-green-200"></div>
              <span className="text-gray-600">활성화 시간</span>
            </div>
          </div>
        </div>
      </div>
      <BottomSheet
        isOpen={isModalOpen}
        mainText="시간 선택"
        onClose={() => setIsModalOpen(false)}
        onButtonClick={handleSaveTime}
        buttonText="저장하기"
        disableDrag={true}
      >
        <div className="flex justify-center py-4 space-x-6">
          {/* 시간 선택 */}
          <div
            ref={hourScrollRef}
            className="overflow-y-auto max-h-60 w-24 text-center relative"
          >
            {Array.from({ length: 24 }, (_, hour) => {
              const hourStr = String(hour).padStart(2, "0");
              return (
                <button
                  key={hourStr}
                  id={`hour-item-${hourStr}`}
                  className={`w-full py-2 text-lg ${
                    selectedHour === hourStr
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100"
                  }`}
                  onClick={(e) => {
                    setSelectedHour(hourStr);
                  }}
                >
                  {hourStr}
                </button>
              );
            })}
          </div>
          <span className="text-lg font-bold pt-2">:</span>
          {/* 분 선택 */}
          <div
            ref={minuteScrollRef}
            className="overflow-y-auto max-h-60 w-24 text-center relative"
          >
            {Array.from({ length: 12 }, (_, i) => {
              const minute = String(i * 5).padStart(2, "0");
              return (
                <button
                  key={minute}
                  id={`minute-item-${minute}`}
                  className={`w-full py-2 text-lg ${
                    selectedMinute === minute
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100"
                  }`}
                  onClick={(e) => {
                    setSelectedMinute(minute);
                  }}
                >
                  {minute}
                </button>
              );
            })}
          </div>
        </div>
      </BottomSheet>

      {/* 모든 요일이 비활성화되었을 때 비활성화 이유를 선택하는 모달 */}
      <ReasonModal
        isOpen={isReasonModalOpen}
        onClose={() => setIsReasonModalOpen(false)}
        onSubmit={handleReasonSubmit}
      />

      <div className="flex justify-center mt-10 mx-4">
        <div
          onClick={handleSubmit}
          className="bg-blue-500 px-3 py-2 border rounded text-white text-xl font-semibold cursor-pointer"
        >
          저장하기
        </div>
      </div>
    </div>
  );
};

export default PickupTimeUpdate;
